import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useToast } from '../../ds/components';
import { errorMessage } from '../../lib/api';
import {
  releaseReservation,
  remindReservation,
  undoReservation,
  useInvalidateReservations,
  withdrawReservation,
  type ReservationItem,
} from './api';

export type ReservationAction = 'remind' | 'withdraw' | 'release' | 'undo';

export interface ReservationActions {
  /** „Erneut erinnern" / „Erneut senden" → `POST /reservations/:id/remind`. */
  remind: () => Promise<ReservationItem | null>;
  /** „Reservierung zurücknehmen" (before the signature) → the undo toast of Reservierung 13. */
  withdraw: (reason?: string) => Promise<ReservationItem | null>;
  /** „Reservierung aufheben" (after the signature) → the undo toast of Reservierung 13. */
  release: (body: { reason: string; note?: string }) => Promise<ReservationItem | null>;
  /** „Rückgängig" within the 30-second window. */
  undo: () => Promise<ReservationItem | null>;
  /** End of the current undo window (ISO) while a withdrawal or release can still be undone. */
  undoUntil: string | null;
  /**
   * The undo toast is mounted through the app's `ToastProvider` (it survives closing dialogs and
   * navigating); nothing has to be rendered here — the field stays for the shared contract.
   */
  toast: ReactNode;
  /** The action currently waiting for the server. */
  pending: ReservationAction | null;
}

/**
 * The reservation actions with the undo toast of Reservierung 13: „Reservierung WE 1.04 zurückgenommen ·
 * noch 24 s · Rückgängig" while `undoUntil` lies in the future; errors arrive as plain toasts.
 */
export function useReservationActions(reservationId: string): ReservationActions {
  const { toast } = useToast();
  const invalidate = useInvalidateReservations();
  const [undoUntil, setUndoUntil] = useState<string | null>(null);
  const [pending, setPending] = useState<ReservationAction | null>(null);
  const undoHandle = useRef<{ dismiss: () => void } | null>(null);

  const undo = useCallback(async (): Promise<ReservationItem | null> => {
    setPending('undo');
    try {
      const item = await undoReservation(reservationId);
      undoHandle.current?.dismiss();
      undoHandle.current = null;
      setUndoUntil(null);
      await invalidate();
      toast({ message: `Rückgängig gemacht · ${item.unit.number} ist wieder reserviert` });
      return item;
    } catch (error) {
      toast({ message: errorMessage(error) });
      setUndoUntil(null);
      await invalidate();
      return null;
    } finally {
      setPending(null);
    }
  }, [invalidate, reservationId, toast]);

  const showUndo = useCallback(
    (item: ReservationItem, verb: string) => {
      const until = item.undoUntil;
      const remaining = until === null ? 0 : new Date(until).getTime() - Date.now();
      if (until === null || remaining <= 0) {
        toast({ message: `Reservierung ${item.unit.number} ${verb}` });
        return;
      }
      setUndoUntil(until);
      undoHandle.current = toast({
        message: `Reservierung ${item.unit.number} ${verb} · Rückgängig möglich`,
        duration: remaining,
        countdown: true,
        action: { label: 'Rückgängig', onClick: () => void undo() },
        onExpire: () => {
          setUndoUntil(null);
          undoHandle.current = null;
          void invalidate();
        },
      });
    },
    [invalidate, toast, undo],
  );

  const run = useCallback(
    async (
      action: ReservationAction,
      call: () => Promise<ReservationItem>,
      after: (item: ReservationItem) => void,
    ): Promise<ReservationItem | null> => {
      setPending(action);
      try {
        const item = await call();
        await invalidate();
        after(item);
        return item;
      } catch (error) {
        toast({ message: errorMessage(error) });
        return null;
      } finally {
        setPending(null);
      }
    },
    [invalidate, toast],
  );

  const remind = useCallback(
    () =>
      run(
        'remind',
        () => remindReservation(reservationId),
        (item) =>
          toast({
            message: `Erinnerung gesendet${item.contact.email ? ` an ${item.contact.email}` : ''}`,
          }),
      ),
    [reservationId, run, toast],
  );

  const withdraw = useCallback(
    (reason?: string) =>
      run(
        'withdraw',
        () => withdrawReservation(reservationId, reason),
        (item) => showUndo(item, 'zurückgenommen'),
      ),
    [reservationId, run, showUndo],
  );

  const release = useCallback(
    (body: { reason: string; note?: string }) =>
      run(
        'release',
        () => releaseReservation(reservationId, body),
        (item) => showUndo(item, 'aufgehoben'),
      ),
    [reservationId, run, showUndo],
  );

  return { remind, withdraw, release, undo, undoUntil, toast: null, pending };
}
